import { useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { CheckCircle2, Inbox, Loader2, PenLine, RotateCcw, Users, X } from 'lucide-react'
import DecideButtons from './DecideButtons.jsx'
import api from '../../services/api'
import { formatDateTime } from '../../utils/helpers'

/**
 * Approvals — everything waiting on you, across every dossier you can open (routers/approvals.py).
 *
 *  • Waiting for your approval: approve with your typed name, or send back with a reason
 *  • Sent back to you: open the dossier and send a corrected version
 *  • Waiting for you to agree a move: approved documents proposed for the Submission folder
 *
 * The same buttons as on the dossier itself, so nobody has to open ten dossiers to clear ten documents.
 */

const errText = (e, fallback) => {
  const d = e?.response?.data?.detail
  return typeof d === 'string' ? d : fallback
}

function Section({ id, icon, title, empty, rows, children }) {
  const Icon = icon
  return (
    <div id={id} className="bg-white border border-gray-200 rounded-xl overflow-hidden scroll-mt-4">
      <div className="px-4 py-3 border-b border-gray-100 bg-gray-50 flex items-center gap-2">
        <Icon className="w-4 h-4 text-blue-600" />
        <h2 className="text-sm font-semibold text-gray-800">{title}</h2>
        <span className="text-xs text-gray-500">· {rows.length}</span>
      </div>
      {rows.length === 0
        ? <p className="px-4 py-4 text-sm text-gray-400">{empty}</p>
        : <ul className="divide-y divide-gray-100">{rows.map(children)}</ul>}
    </div>
  )
}

function Where({ r }) {
  return (
    <div className="min-w-0 mr-auto">
      <p className="text-xs font-mono text-gray-500">{r.doc_number}{r.version > 0 ? ` · v${r.version}` : ''}</p>
      <p className="text-sm font-medium text-gray-900">{r.title}</p>
      <p className="text-xs text-gray-500">
        <Link to={r.link} className="text-blue-600 hover:underline">{r.dossier_title}</Link>
        {' · '}{r.team}{' · '}{r.folder === 'submission' ? 'Submission folder' : 'Working folder'}
        {r.by ? ` · put in by ${r.by}` : ''}{r.since ? ` · ${formatDateTime(r.since)}` : ''}
      </p>
    </div>
  )
}

export default function Approvals() {
  const [data, setData] = useState(null)
  const [notice, setNotice] = useState(null)
  const [busy, setBusy] = useState(null)

  const load = () => api.get('/approvals/').then(r => setData(r.data))
    .catch(e => setNotice({ kind: 'error', text: errText(e, 'Could not load your approvals.') }))
  useEffect(() => { load() }, [])
  // Arrived from "Sent back to me": that list, in view.
  const [params] = useSearchParams()
  useEffect(() => {
    if (data && params.get('show')) document.getElementById(params.get('show'))?.scrollIntoView({ behavior: 'smooth' })
  }, [data, params])

  const base = (r) => `/projects/${r.team_id}/dossiers/${r.dossier_id}`
  const run = async (key, fn) => {
    setBusy(key); setNotice(null)
    try {
      const res = await fn()
      setNotice({ kind: 'ok', text: res?.data?.detail || 'Done.' })
      await load()
      return true
    } catch (e) {
      setNotice({ kind: 'error', text: errText(e, 'That did not work.') })
      return false
    } finally { setBusy(null) }
  }
  if (!data) {
    return notice ? <div className="p-6 text-sm text-red-600">{notice.text}</div>
      : <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-blue-600" /></div>
  }

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-5">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2"><CheckCircle2 className="w-6 h-6 text-blue-600" /> Approvals</h1>
        <p className="text-sm text-gray-500 mt-1">Everything waiting on you, across all your dossiers. Oldest first.</p>
      </div>

      {notice && (
        <div className={`rounded-lg border px-4 py-2.5 text-sm flex items-center justify-between ${notice.kind === 'error'
          ? 'bg-red-50 border-red-200 text-red-700' : 'bg-green-50 border-green-200 text-green-800'}`}>
          {notice.text}
          <button onClick={() => setNotice(null)}><X className="w-4 h-4" /></button>
        </div>
      )}

      <Section icon={PenLine} title="Waiting for your approval" rows={data.approve} empty="Nothing waits for your approval.">
        {r => (
          <li key={`a-${r.item_id}`} className="px-4 py-3 flex flex-wrap items-center gap-2">
            <Where r={r} />
            {r.final && <span className="text-[11px] px-2 py-0.5 rounded-full border bg-green-50 text-green-700 border-green-200">Final approval</span>}
            <DecideButtons row={r} onDone={(text) => { setNotice({ kind: 'ok', text }); load() }}
                           onError={(text) => setNotice({ kind: 'error', text })} />
          </li>
        )}
      </Section>

      <Section id="sent_back" icon={RotateCcw} title="Sent back to you" rows={data.sent_back} empty="Nothing has been sent back to you.">
        {r => (
          <li key={`s-${r.item_id}`} className="px-4 py-3 flex flex-wrap items-center gap-2">
            <div className="min-w-0 mr-auto">
              <Where r={r} />
              <p className="text-xs text-red-700 mt-1">Sent back by {r.sent_back_by || '—'}: “{r.reason}”</p>
            </div>
            <Link to={r.link} className="px-2.5 py-1 rounded-lg border border-gray-300 text-xs text-gray-700 hover:bg-gray-50">
              Open and send a new version
            </Link>
          </li>
        )}
      </Section>

      <Section icon={Users} title="Waiting for you to agree a move" rows={data.agree}
               empty="No move to the Submission folder waits for you.">
        {r => (
          <li key={`g-${r.item_id}`} className="px-4 py-3 flex flex-wrap items-center gap-2">
            <Where r={r} />
            <span className="text-xs text-gray-500">{r.waiting} still to agree</span>
            <button onClick={() => run(`ag-${r.item_id}`, () => api.post(`${base(r)}/agree`, { item_ids: [r.item_id] }))}
                    disabled={busy === `ag-${r.item_id}`}
                    className="px-2.5 py-1 rounded-lg bg-green-600 text-white text-xs hover:bg-green-700 disabled:opacity-40">
              Agree to move
            </button>
          </li>
        )}
      </Section>

      {!data.approve.length && !data.sent_back.length && !data.agree.length && (
        <p className="text-sm text-gray-500 flex items-center gap-2"><Inbox className="w-4 h-4" /> All clear — nothing waits on you.</p>
      )}

    </div>
  )
}
